// web/src/utils/labels/common.ts —— 通用：部门 / dialplan 目标 / 场景 / 挂断原因 / 状态 / 内置标识
// 拆自 web/src/utils/labels.ts 的同名分区（内容原样搬运）；由 web/src/utils/labels.ts 以 export * 聚合，导出面对调用方不变。

import { maskPhone } from '../format';
import type { PillColor } from '../../components/PillTag';

import { qualityRuleTypeLabel } from './assist-ai-intent';

/** 内置 dialplan 目标（FreeSWITCH 分机名，落到 CDR callee 字段，非可拨号码） */
export const DIALPLAN_TARGET_LABELS: Record<string, string> = {
  support_queue: '客服支持队列',
  survey: '满意度调查',
  queue_timeout: '排队超时',
  voicemail: '语音信箱',
  ai_outbound: 'AI 外呼',
  bcc_ivr_to_ai: 'IVR 转 AI',
  ob_agent: '外呼人工',
};

/** 默认队列/技能组历史英文值（= 后端 DefaultSkillGroupName「客服支持」的历史值） */
export const DEFAULT_GROUP_LABELS: Record<string, string> = {
  support: '客服支持',
};

/** 外呼场景类型（outbound scenario.scene_type）→ 中文 */
export const SCENE_TYPE_LABELS: Record<string, string> = {
  marketing: '营销',
  return_visit: '回访',
  collection: '催缴',
  // 通知类场景：活动通知/用户唤醒/服务通知等批量告知外呼均挂 notice 类型，名称区分具体场景
  notice: '通知',
};

/** FreeSWITCH hangup_cause → 中文映射（未知原因原样展示） */
export const HANGUP_CAUSE_LABELS: Record<string, string> = {
  NORMAL_CLEARING: '正常挂断',
  ORIGINATOR_CANCEL: '主叫取消',
  NO_ANSWER: '无应答',
  NO_ROUTE_DESTINATION: '路由不可达',
  USER_BUSY: '用户忙',
  CALL_REJECTED: '呼叫被拒绝',
  NO_USER_RESPONSE: '用户无响应',
  USER_NOT_REGISTERED: '用户未注册',
  UNALLOCATED_NUMBER: '空号',
  INVALID_NUMBER_FORMAT: '号码格式错误',
  DESTINATION_OUT_OF_ORDER: '对端不可达',
  NETWORK_OUT_OF_ORDER: '网络故障',
  NORMAL_TEMPORARY_FAILURE: '临时故障',
  NORMAL_UNSPECIFIED: '未知原因',
  CONGESTION: '网络拥塞',
  NO_ANSWER_TIMEOUT: '无应答超时',
  INCOMPATIBLE_DESTINATION: '终端不兼容',
  MEDIA_TIMEOUT: '媒体超时',
  ALLOTTED_TIMEOUT: '呼叫超时',
  RECOVERY_ON_TIMER_EXPIRE: '应答超时',
  LOSE_RACE: '竞争失败',
  MANAGER_REQUEST: '管理操作',
  SYSTEM_SHUTDOWN: '系统关闭',
  // 引擎侧队列键装不下（技能组 code 超出交换层缓冲区）时主动挂断：属配置错误而非话务结果，
  // 文案要指向可修复对象，否则运维只看到一串英文码。见 docs/skill-group-queue-key.md §1。
  KEY_OVERFLOW: '队列键超长（技能组配置错误）',
};

/** 通用状态标签 */
export const STATUS_LABELS: Record<string, string> = {
  open: '待处理',
  processing: '处理中',
  resolved: '已解决',
  closed: '已关闭',
  completed: '已完成',
  idle: '示闲',
  break: '小休',
  offline: '下线',
  busy: '示忙',
  talking: '通话中',
  after_work: '话后处理',
  ringing: '振铃',
  acw: '话后处理',
  after_call_work: '话后处理',
  answered: '已接通',
  missed: '未接',
  failed: '失败',
  voicemail: '语音信箱',
  inbound: '呼入',
  outbound: '呼出',
  queued: '排队中',
  robot: '机器人',
  human: '人工',
  finished: '已结束',
  draft: '草稿',
  pending: '待启动',
  running: '进行中',
  paused: '已暂停',
  done: '已完成',
  skipped: '已跳过',
  // 队列状态
  normal: '正常',
  overflow: '溢出',
  full: '已满',
  low: '低',
  medium: '中',
  high: '高',
  urgent: '紧急',
  enabled: '启用',
  disabled: '停用',
  // 营销产品上下架状态（marketing_product.status）
  on_sale: '在售',
  off_sale: '停售',
  synced: '已同步',
  waiting: '待呼叫',
  calling: '呼叫中',
  connected: '已接通',
  no_answer: '未应答',
  blacklisted: '已拉黑',
  success: '成功',
  admin: '管理员',
  supervisor: '主管',
  // 组内角色（已合并为角色码）：角色列表未加载时 roleLabel 靠这里兜底显示
  'supervisor.leader': '班组长',
  'supervisor.deputy': '副组长',
  agent: '坐席',
  login: '登录',
  login_failed: '登录失败',
  create: '新建',
  update: '更新',
  delete: '删除',
  export: '导出',
  reset_password: '重置密码',
  claim: '认领',
  transfer: '转接',
  sync: '同步',
  ai: 'AI',
  call: '语音',
  chat: '在线',
  // 质检规则类型（文本/实时/流程类，见 qualityRuleTypeLabel 出口）
  keyword: '关键词',
  forbidden: '违禁语',
  silence: '静默时长',
  llm: 'LLM 评分',
  overlap: '实时抢话',
  consistency: '业务一致性',
  answer: '答案比对',
  must_say: '必说话术',
  emotion: '情绪趋势',
  // 质检违规点复核结论
  UNVERIFIED: '待复核',
  VERIFIED: '已确认',
  MISJUDGED: '已误判',
  CORRECTED: '已改分',
  // 申诉裁定
  upheld: '维持原判',
  revoked: '申诉通过',
  rescore: '改分重评',
  // 质检规则类型（流程类，与上方文本/实时类同属 qualityRuleTypeLabel 出口）
  process_dwell: '流程驻留',
  process_coverage: '流程覆盖',
  process_keyword: '流程关键词',
  process_llm: '流程 LLM',
  process_sla: '流程时效',
};

/** 内置标识（dialplan 目标 + 默认组名）统一映射表 */
const BUILTIN_LABELS: Record<string, string> = {
  ...DIALPLAN_TARGET_LABELS,
  ...DEFAULT_GROUP_LABELS,
};

/** 内置标识（dialplan 目标 + 默认组名）→ 中文，未知值原样返回 */
export function builtinLabel(v?: string): string {
  if (!v) return v ?? '';
  return BUILTIN_LABELS[v] || v;
}

/** 外呼场景类型标签，未知值原样返回 */
export function sceneTypeLabel(v?: string): string {
  if (!v) return '-';
  return SCENE_TYPE_LABELS[v] || v;
}

/** 外呼任务形态（outbound task.type：ai=AI 外呼 / agent=坐席外呼 / ivr=IVR 评分外呼）→ 中文 */
export const OUTBOUND_TASK_TYPE_LABELS: Record<string, string> = {
  ai: 'AI 外呼',
  agent: '坐席外呼',
  ivr: 'IVR 评分外呼',
};

/** 外呼任务形态标签，未知值原样返回 */
export function outboundTaskTypeLabel(v?: string): string {
  if (!v) return '-';
  return OUTBOUND_TASK_TYPE_LABELS[v] || v;
}

/** 是否为内置 dialplan 目标（不可拨号码，展示时不可点击回拨） */
export function isDialplanTarget(v?: string): boolean {
  return !!v && v in DIALPLAN_TARGET_LABELS;
}

/** 通用状态标签（兼容 StatusTag 旧导入，未知值原样返回） */
export function statusLabel(v?: string): string {
  if (!v) return '-';
  return STATUS_LABELS[v] || v;
}

/** 挂断原因标签（兼容 api/cdr 旧导入，未知值原样返回）。原先寄居在已删除的知识库动作分片里，现搬回原因表本部 */
export function hangupCauseLabel(cause?: string, connected?: boolean): string {
  if (!cause) return '-';
  // 从未接通（answer_time 为空）的 NORMAL_CLEARING 不是「正常挂断」，应显示未接通
  if (cause === 'NORMAL_CLEARING' && connected === false) return '未接通';
  return HANGUP_CAUSE_LABELS[cause] || cause;
}

